// Ordregang (kartmanøver fase 3): når en enhet FLYTTES på kartet, skrives en
// fempunktsordre automatisk av @skade/hildr — det er dette som lukker
// Orient→Decide i OODA raskt nok til å være nyttig.
//
// Delingen her er bevisst: alt som kan regnes ut er REN funksjon (testbart uten
// DOM, siden vitest kjører i node uten jsdom), og panelet er et tynt DOM-lag
// oppå. Forflytningsmåte (til fots / kolonne / kjøretøy / heli / båt) velges av
// operatøren og regenererer ordren — det er fase 6 (infil/exfil) sitt fundament.
import { forward as mgrsForward } from 'mgrs';
import {
  ILDAPNING, ILDAPNING_VALG, METHODS, buildMovementOrder, irfmiForForflytning,
  irfmiSammendrag, irfmiTilTekst, manglerVedBestemmelse, orderSummary, velgOrdreformat,
  type Fempunktsordre, type IldapningsType, type Irfmi, type MoveMethod,
  type MovementOrderInput, type Ordreformat,
} from '@skade/hildr';

export interface OrdrePunkt { lon: number; lat: number }

export interface OrdreEnhet {
  /** ORBAT-unit-id når enheten er koblet, ellers kart-lokal id. */
  id: string;
  label: string;
  echelon?: string;
}

/** Alt panelet trenger for å skrive en ordre. Rent data — ingen DOM. */
export interface OrdreUtkast {
  enhet: OrdreEnhet;
  fra: OrdrePunkt;
  til: OrdrePunkt;
  metode: MoveMethod;
  startTsMs: number;
  /** Utsteder (TOC / avsenderens rolle+distrikt). */
  utsteder?: string;
  net?: string;
  kallesignal?: string;
  /** Sjefens hensikt, fritt felt fra operatøren. */
  hensikt?: string;
  /** Ordreformat. Utledes av echelon (GRUS: lag/tropp → IRFMI), kan overstyres. */
  format?: Ordreformat;
  /** Ildåpningsbestemmelse (GRUS s. 53) — settes i IRFMI-ens ildledelsespunkt. */
  ildapning?: IldapningsType;
  /** Ildåpningsavstand i meter (kreves når type = avstand). */
  ildapningAvstandM?: number;
  /** Ildåpningslinje i lendet (kreves når type = ildapningslinje). */
  ildapningLinje?: string;
}

/**
 * Bygg IRFMI for forflytningen — lagførers minimumsordre (GRUS s. 51).
 * Brukes når enheten er på lags-/troppsnivå: en fempunktsordre er for treg å
 * gi muntlig i stridskontakt.
 */
export function irfmiFraUtkast(u: OrdreUtkast): Irfmi {
  return irfmiForForflytning({
    til: u.enhet.label,
    fra: u.utsteder,
    fraPunkt: { lat: u.fra.lat, lon: u.fra.lon },
    tilPunkt: { lat: u.til.lat, lon: u.til.lon },
    tsMs: u.startTsMs,
    enhetId: u.enhet.id,
    innledning: 'Forflytning',
    metode: `${METHODS[u.metode].label} — ${METHODS[u.metode].konsept}`,
    ildapning: u.ildapning
      ? { type: u.ildapning, avstandM: u.ildapningAvstandM, linje: u.ildapningLinje }
      : undefined,
  });
}

/** Formatet ordren skal ha: fra utkastet hvis satt, ellers utledet av echelon. */
export function formatForUtkast(u: OrdreUtkast): Ordreformat {
  return u.format ?? velgOrdreformat(u.enhet.echelon);
}

/** MGRS med 5-sifret presisjon, eller null utenfor gyldig sone. */
export function mgrsEller(p: OrdrePunkt): string | undefined {
  try { return mgrsForward([p.lon, p.lat], 5); } catch { return undefined; }
}

/** Rekkefølgen metodene tilbys i — tyngste transport sist, til fots først. */
export const METODE_VALG: MoveMethod[] = ['tilfots', 'kolonne', 'kjoretoy', 'heli', 'bat'];

/** Norsk etikett + doktrinær marsjfart for en metode (fra @skade/hildr). */
export function metodeEtikett(m: MoveMethod): string {
  const spec = METHODS[m];
  return `${spec.label} · ${spec.speedKmh} km/t`;
}

/**
 * Bygg ordren fra utkastet. Deterministisk: samme utkast → samme ordre (inkl.
 * ordre-id og DTG), slik at et panel kan regenerere uten å produsere «ny» ordre.
 */
export function ordreFraUtkast(u: OrdreUtkast): Fempunktsordre {
  const inn: MovementOrderInput = {
    unit: { id: u.enhet.id, label: u.enhet.label, echelon: u.enhet.echelon },
    from: { lat: u.fra.lat, lon: u.fra.lon },
    to: { lat: u.til.lat, lon: u.til.lon },
    method: u.metode,
    startTsMs: u.startTsMs,
    fromMgrs: mgrsEller(u.fra),
    toMgrs: mgrsEller(u.til),
    issuer: u.utsteder,
    net: u.net,
    callsign: u.kallesignal,
    intent: u.hensikt?.trim() || undefined,
  };
  return buildMovementOrder(inn);
}

/**
 * Ordren som ren tekst for sending på samband/chat. Formatet følger
 * fempunktsordren (SMEAC) med nummererte punkter, fordi det er slik den leses
 * opp — ikke som en JSON-dump.
 */
export function ordreTilTekst(o: Fempunktsordre): string {
  return [
    `FEMPUNKTSORDRE ${o.ordreId}`,
    `DTG ${o.dtg} · FRA ${o.fra} · TIL ${o.til}`,
    '',
    '1. SITUASJON',
    `   Fiende: ${o.situasjon.fiende}`,
    `   Egne: ${o.situasjon.egne}`,
    `   Sivile forhold: ${o.situasjon.sivileForhold}`,
    `   Forsterkninger/avgivelser: ${o.situasjon.forsterkninger}`,
    `   Terreng/vær: ${o.situasjon.terrengVaer}`,
    '2. OPPDRAG',
    `   ${o.oppdrag}`,
    '3. UTFØRELSE',
    `   Hensikt: ${o.utforelse.hensikt}`,
    `   Konsept: ${o.utforelse.konsept}`,
    ...o.utforelse.faser.map((f) => `   ${f}`),
    '   Oppdrag til underavdelinger:',
    ...o.utforelse.underavdelinger.map((t) => `     - ${t}`),
    '   Koordinering:',
    ...o.utforelse.koordinering.map((k) => `     - ${k}`),
    '4. ADMINISTRASJON OG FORSYNING',
    `   ${o.adminForsyning}`,
    '5. LEDELSE OG SAMBAND',
    `   ${o.ledelseSamband}`,
  ].join('\n');
}

/** Kort status-linje til kart-popup og logg. */
export function ordreLinje(o: Fempunktsordre): string {
  return orderSummary(o);
}

/**
 * Sporet forflytningen følger, som GeoJSON-linje med tidsstempler i egenskapene.
 * Driver kart-spor + tidslinje (fase 4): hvert punkt har `tsMs`, så tidsmaskinen
 * kan interpolere hvor enheten SKAL være på et gitt tidspunkt.
 */
export function forflytningsSpor(o: Fempunktsordre, steg = 12): {
  type: 'Feature';
  geometry: { type: 'LineString'; coordinates: [number, number][] };
  properties: { ordreId: string; startTsMs: number; ankomstTsMs: number; tider: number[] };
} {
  const { from, to } = { from: o.movement.from, to: o.movement.to };
  const coords: [number, number][] = [];
  const tider: number[] = [];
  const n = Math.max(1, Math.floor(steg));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    coords.push([from.lon + (to.lon - from.lon) * t, from.lat + (to.lat - from.lat) * t]);
    tider.push(Math.round(o.movement.startTsMs + (o.movement.ankomstTsMs - o.movement.startTsMs) * t));
  }
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: coords },
    properties: {
      ordreId: o.ordreId,
      startTsMs: o.movement.startTsMs,
      ankomstTsMs: o.movement.ankomstTsMs,
      tider,
    },
  };
}

/**
 * Interpolér forventet posisjon på et gitt tidspunkt. Før oppstart = FRA-punktet,
 * etter beregnet ankomst = TIL-punktet (enheten står, den fortsetter ikke forbi).
 * Dette er «vis forflytningen over tid» i sin enkleste ærlige form: en PLANLAGT
 * posisjon, ikke en målt. Ekte posisjon kommer fra BFT/PLI når enheten melder.
 */
export function forventetPosisjon(o: Fempunktsordre, tsMs: number): OrdrePunkt & { planlagt: true } {
  const { startTsMs, ankomstTsMs, from, to } = {
    startTsMs: o.movement.startTsMs, ankomstTsMs: o.movement.ankomstTsMs,
    from: o.movement.from, to: o.movement.to,
  };
  const spenn = ankomstTsMs - startTsMs;
  const t = spenn <= 0 ? 1 : Math.min(1, Math.max(0, (tsMs - startTsMs) / spenn));
  return {
    lon: from.lon + (to.lon - from.lon) * t,
    lat: from.lat + (to.lat - from.lat) * t,
    planlagt: true,
  };
}

// ── Panel (tynt DOM-lag over den rene modellen over) ────────────────────────
// Bruker `.report-composer` som de andre panelene, slik at vindushåndteringen
// (flytt/skaler/én-skjerm) og pop-out virker uten egen wiring.

export interface OrdrePanelHooks {
  /** Send ordren på samband (chat/nett). Returner false hvis sending feilet. */
  send?: (tekst: string, ordre: Fempunktsordre) => boolean | void;
  /** Tegn/fjern forflytningssporet på kartet. */
  visSpor?: (spor: ReturnType<typeof forflytningsSpor> | null) => void;
  varsle?: (tekst: string) => void;
  /** Overstyrbar «nå» (test). */
  now?: () => number;
}

export class OrdrePanel {
  readonly el: HTMLElement;
  private open = false;
  private utkast: OrdreUtkast | null = null;
  private ordre: Fempunktsordre | null = null;
  private irfmi: Irfmi | null = null;
  private kropp!: HTMLElement;
  private undertittel!: HTMLElement;
  private sendt = false;

  constructor(private hooks: OrdrePanelHooks = {}) {
    this.el = this.bygg();
  }

  private bygg(): HTMLElement {
    const p = lag('div', 'report-composer ordre-panel');
    const hode = lag('div', 'report-head');
    hode.append(tekst('report-title', 'Fempunktsordre — forflytning'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×';
    x.title = 'Lukk (ordren forkastes ikke — «Gi ordre …» på enheten henter den fram igjen)';
    x.onclick = () => this.lukk();
    hode.append(x);
    p.append(hode);
    this.undertittel = lag('div', 'ordre-sub');
    // Tomtilstanden må stemme med utløseren: en dragning SKRIVER ikke lenger
    // ordren av seg selv — den tilbyr den. Sto det «flytt en enhet for å skrive
    // en ordre», ville teksten love noe som ikke skjer.
    this.undertittel.textContent = 'Flytt en enhet på kartet og velg «Skriv ordre» — eller «Gi ordre …» på enheten.';
    p.append(this.undertittel);
    this.kropp = lag('div', 'ordre-kropp');
    p.append(this.kropp);
    return p;
  }

  erApen(): boolean { return this.open; }
  gjeldendeOrdre(): Fempunktsordre | null { return this.ordre; }

  lukk() {
    this.open = false;
    this.el.classList.remove('open');
    this.hooks.visSpor?.(null);
  }

  /**
   * Skriv ordren for en forflytning og vis den.
   *
   * ⚠ Kalles nå bare etter et EKSPLISITT valg fra operatøren («Skriv ordre» i
   * tilbudet etter en dragning, eller «Gi ordre …» i enhetens meny). Tidligere
   * ble den kalt direkte fra `units.onMoved`, altså av hver dragning — en
   * konsekvensrik handling utløst av en håndbevegelse. Innholdet i ordren er
   * uendret; det var utløseren som var feil.
   */
  visForFlytting(enhet: OrdreEnhet, fra: OrdrePunkt, til: OrdrePunkt, utsteder?: string, net?: string) {
    this.utkast = {
      enhet, fra, til,
      metode: 'kolonne', // vanligste for HV-forflytning; operatøren bytter under
      startTsMs: this.hooks.now?.() ?? Date.now(),
      utsteder, net,
    };
    this.sendt = false;
    this.open = true;
    this.el.classList.add('open');
    this.regenerer();
  }

  private regenerer() {
    if (!this.utkast) return;
    // Fempunktsordren bygges alltid — den bærer den maskinlesbare forflytningen
    // som driver kart-spor og tidslinje, uansett hvilket format som VISES.
    this.ordre = ordreFraUtkast(this.utkast);
    this.irfmi = formatForUtkast(this.utkast) === 'irfmi' ? irfmiFraUtkast(this.utkast) : null;
    this.hooks.visSpor?.(forflytningsSpor(this.ordre));
    this.render();
  }

  /** Formatvelger + ildåpningsbestemmelse (GRUS). */
  private renderGrusValg(u: OrdreUtkast): HTMLElement {
    const g = lag('div', 'ordre-grus');
    const fmt = formatForUtkast(u);

    const fRad = lag('div', 'ordre-metoder');
    fRad.append(tekst('ordre-lbl', 'Ordreformat'));
    const fKnapper = lag('div', 'ordre-metode-knapper');
    for (const [v, navn, hjelp] of [
      ['irfmi', 'IRFMI (lag/tropp)', 'Lagførers minimumsordre — kort nok å gi muntlig (GRUS s. 51)'],
      ['fempunktsordre', 'Fempunktsordre', 'Full SMEAC — kompani og over'],
    ] as const) {
      const b = document.createElement('button');
      b.className = 'ordre-metode' + (fmt === v ? ' valgt' : '');
      b.textContent = navn;
      b.title = hjelp;
      b.setAttribute('aria-pressed', String(fmt === v));
      b.onclick = () => { u.format = v; this.sendt = false; this.regenerer(); };
      fKnapper.append(b);
    }
    fRad.append(fKnapper);
    g.append(fRad);
    if (!u.format && u.enhet.echelon) {
      const n = lag('div', 'ordre-note');
      n.textContent = `Format utledet av nivå «${u.enhet.echelon}» (GRUS: lag og tropp bruker IRFMI).`;
      g.append(n);
    }

    // Ildåpningsbestemmelse — doktrinær, ikke fritekst.
    const iRad = lag('div', 'ordre-metoder');
    iRad.append(tekst('ordre-lbl', 'Ildåpningsbestemmelse (GRUS s. 53)'));
    const iKnapper = lag('div', 'ordre-metode-knapper');
    for (const t of ILDAPNING_VALG) {
      const b = document.createElement('button');
      b.className = 'ordre-metode' + (u.ildapning === t ? ' valgt' : '');
      b.textContent = ILDAPNING[t].navn;
      b.title = `${ILDAPNING[t].betydning}\n\nBruk: ${ILDAPNING[t].bruk}`;
      b.setAttribute('aria-pressed', String(u.ildapning === t));
      b.onclick = () => { u.ildapning = u.ildapning === t ? undefined : t; this.sendt = false; this.regenerer(); };
      iKnapper.append(b);
    }
    iRad.append(iKnapper);
    g.append(iRad);

    // Parameter der bestemmelsen er ubrukelig uten (avstand i m / linje i lendet).
    if (u.ildapning) {
      const def = ILDAPNING[u.ildapning];
      if (def.krever === 'avstandM') {
        const inn = document.createElement('input');
        inn.className = 'ordre-input'; inn.type = 'number'; inn.min = '1';
        inn.placeholder = 'Ildåpningsavstand i meter';
        inn.setAttribute('aria-label', 'Ildåpningsavstand i meter');
        if (u.ildapningAvstandM) inn.value = String(u.ildapningAvstandM);
        inn.onchange = () => { u.ildapningAvstandM = Number(inn.value) || undefined; this.regenerer(); };
        g.append(inn);
      } else if (def.krever === 'linje') {
        const inn = document.createElement('input');
        inn.className = 'ordre-input';
        inn.placeholder = 'Ildåpningslinje: kraftlinje, bekk, vei, høydedrag, skogkant';
        inn.setAttribute('aria-label', 'Ildåpningslinje i lendet');
        inn.value = u.ildapningLinje ?? '';
        inn.onchange = () => { u.ildapningLinje = inn.value.trim() || undefined; this.regenerer(); };
        g.append(inn);
      }
      const m = manglerVedBestemmelse({
        type: u.ildapning, avstandM: u.ildapningAvstandM, linje: u.ildapningLinje,
      });
      if (m.length) {
        const v = lag('div', 'ordre-mangel');
        v.textContent = m.join(' · ');
        g.append(v);
      }
    }
    return g;
  }

  private render() {
    const o = this.ordre;
    const u = this.utkast;
    if (!o || !u) return;
    this.undertittel.textContent = this.irfmi ? irfmiSammendrag(this.irfmi) : ordreLinje(o);
    this.kropp.replaceChildren();

    // Forflytningsmåte — styrer tempo/ETA og dermed hele tidslinja.
    const mRad = lag('div', 'ordre-metoder');
    mRad.append(tekst('ordre-lbl', 'Forflytningsmåte'));
    const knapper = lag('div', 'ordre-metode-knapper');
    for (const m of METODE_VALG) {
      const b = document.createElement('button');
      b.className = 'ordre-metode' + (u.metode === m ? ' valgt' : '');
      b.textContent = metodeEtikett(m);
      b.setAttribute('aria-pressed', String(u.metode === m));
      b.onclick = () => { u.metode = m; this.sendt = false; this.regenerer(); };
      knapper.append(b);
    }
    mRad.append(knapper);
    this.kropp.append(mRad);
    this.kropp.append(this.renderGrusValg(u));

    // Sjefens hensikt — fritt felt, regenererer ordren ved endring.
    const hRad = lag('div', 'ordre-hensikt');
    hRad.append(tekst('ordre-lbl', 'Sjefens hensikt (valgfritt)'));
    const felt = document.createElement('input');
    felt.className = 'ordre-input';
    felt.placeholder = 'f.eks. sikre brohodet og hindre fiendtlig fremrykning';
    felt.value = u.hensikt ?? '';
    felt.onchange = () => { u.hensikt = felt.value; this.sendt = false; this.regenerer(); };
    hRad.append(felt);
    this.kropp.append(hRad);

    // Selve ordren, punkt for punkt. IRFMI har FEM andre punkter enn SMEAC —
    // og tomme punkter kuttes, slik GRUS krever.
    const ir = this.irfmi;
    const punkter: [string, string][] = ir ? [
      ['I — Innledning', ir.innledning],
      ['R — Retning', [ir.retning.tilFienden && `fienden: ${ir.retning.tilFienden}`,
        ir.retning.tilEgne && `egne: ${ir.retning.tilEgne}`,
        ir.retning.forFremrykning && `fremrykning: ${ir.retning.forFremrykning}`]
        .filter(Boolean).join('\n') || 'som nå'],
      ['F — Formasjon', ir.formasjon],
      ['M — Metode / kort plan', ir.metode],
      ['I — Innbrudd · ildledelse · iverksettelse', [ir.innbruddssted, ir.ildledelse, ir.iverksettelse]
        .filter(Boolean).join('\n')],
    ] : [
      ['1. Situasjon', [
        `Fiende: ${o.situasjon.fiende}`,
        `Egne: ${o.situasjon.egne}`,
        `Sivile forhold: ${o.situasjon.sivileForhold}`,
        `Forsterkninger/avgivelser: ${o.situasjon.forsterkninger}`,
        o.situasjon.terrengVaer,
      ].join('\n')],
      ['2. Oppdrag', o.oppdrag],
      ['3. Utførelse', [
        `Hensikt: ${o.utforelse.hensikt}`,
        o.utforelse.konsept,
        ...o.utforelse.faser,
        'Oppdrag til underavdelinger:',
        ...o.utforelse.underavdelinger.map((t) => `  - ${t}`),
        'Koordinering:',
        ...o.utforelse.koordinering.map((k) => `  - ${k}`),
      ].join('\n')],
      ['4. Adm. og forsyning', o.adminForsyning],
      ['5. Ledelse og samband', o.ledelseSamband],
    ];
    for (const [t, v] of punkter) {
      const blokk = lag('div', 'ordre-punkt');
      blokk.append(tekst('ordre-punkt-t', t));
      const kropp = lag('div', 'ordre-punkt-v');
      kropp.textContent = v; // textContent: feltdata skal aldri tolkes som HTML
      blokk.append(kropp);
      this.kropp.append(blokk);
    }

    // Handlinger.
    const rad = lag('div', 'ordre-handlinger');
    const send = document.createElement('button');
    send.className = 'ordre-send';
    send.textContent = this.sendt ? 'Sendt ✓' : 'Send på samband';
    send.disabled = this.sendt || !this.hooks.send;
    send.onclick = () => {
      const res = this.hooks.send?.(this.irfmi ? irfmiTilTekst(this.irfmi) : ordreTilTekst(o), o);
      if (res === false) { this.hooks.varsle?.('Ordren kunne ikke sendes — ingen tilkobling.'); return; }
      this.sendt = true;
      this.hooks.varsle?.(`Ordre ${o.ordreId} sendt til ${o.til}.`);
      this.render();
    };
    const kopier = document.createElement('button');
    kopier.className = 'ordre-kopier';
    kopier.textContent = 'Kopiér tekst';
    kopier.onclick = () => {
      void navigator.clipboard?.writeText(this.irfmi ? irfmiTilTekst(this.irfmi) : ordreTilTekst(o));
      this.hooks.varsle?.('Ordren kopiert.');
    };
    rad.append(send, kopier);
    this.kropp.append(rad);

    // Ærlighet om hva ordren IKKE er: ETA er beregnet på marsjfart i luftlinje.
    const note = lag('div', 'ordre-note');
    note.textContent =
      `Marsjtid er beregnet på ${o.movement.speedKmh} km/t i luftlinje (${o.movement.distanceKm} km) — `
      + 'ikke langs vei og uten terrengmotstand. Kontrollér mot rute og lysforhold før den sendes.';
    this.kropp.append(note);
  }
}

function lag(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag); e.className = cls; return e;
}
function tekst(cls: string, t: string): HTMLElement {
  const e = document.createElement('span'); e.className = cls; e.textContent = t; return e;
}
